<template>
  <div class="checkbox-field-response">
    <div class="field-header">
      <h3 class="field-label">
        {{ field.label }}
        <span v-if="field.required" class="required-indicator">*</span>
        <v-chip x-small color="warning" class="ml-2" v-if="field.useAggregate">Auto-calculated</v-chip>
      </h3>
    </div>

    <div v-if="field.description" class="field-description">
      {{ field.description }}
    </div>

    <v-checkbox
      v-for="option in fieldOptions"
      :key="option.id"
      v-model="selectedOptionsMap[option.id]"
      :label="option.label"
      color="primary"
      hide-details
      class="my-1"
      @change="updateSelections"
      :readonly="disabled || isAggregateField"
      :disabled="isAggregateField"
    ></v-checkbox>

    <div v-if="showValidationError" class="error--text mt-2 mb-3">
      {{ validationErrorMessage }}
    </div>

    <v-textarea
      v-model="localResponse.comment"
      :readonly="disabled"
      outlined
      dense
      label="Comment (Optional)"
      placeholder="Add any additional comments here..."
      rows="2"
      auto-grow
      @change="updateResponse"
    ></v-textarea>
  </div>
</template>

<script>
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

export default @Component({}) class CheckboxFieldResponse extends Vue {
  @Prop({ type: Object, required: true }) field;
  @Prop({ type: Object, required: false }) response;
  @Prop({ type: Boolean, required: false }) disabled;

  localResponse = {
    fieldId: 0,
    selectedOptionIds: [],
    comment: ''
  };

  selectedOptionsMap = {};
  showValidationError = false;
  validationErrorMessage = '';

  created() {
    this.initializeResponse();
  }

  @Watch('response')
  onResponseChange() {
    this.initializeResponse();
  }

  initializeResponse() {
    if (this.response) {
      this.localResponse = { ...this.response };

      // Initialize the selected checkboxes
      this.initializeSelectionMap();
    } else {
      this.localResponse = {
        fieldId: this.field.id || 0,
        selectedOptionIds: [],
        comment: ''
      };

      this.initializeSelectionMap();
    }
  }

  initializeSelectionMap() {
    // Reset the map
    this.selectedOptionsMap = {};

    // Initialize all options as unselected
    this.fieldOptions.forEach(option => {
      if (option.id !== undefined) {
        this.selectedOptionsMap[option.id] = false;
      }
    });

    // Mark selected options based on the response
    if (this.localResponse.selectedOptionIds && this.localResponse.selectedOptionIds.length > 0) {
      this.localResponse.selectedOptionIds.forEach(id => {
        if (id !== undefined) {
          this.selectedOptionsMap[id] = true;
        }
      });
    }
  }

  get fieldOptions() {
    return this.field.options || [];
  }

  get isAggregateField() {
    return this.field.useAggregate === true;
  }

  updateSelections() {
    // Convert the selection map to an array of selected option IDs
    const selectedIds = [];

    Object.entries(this.selectedOptionsMap).forEach(([key, value]) => {
      if (value) {
        selectedIds.push(Number(key));
      }
    });

    this.localResponse.selectedOptionIds = selectedIds;

    // Validate selection
    this.validateSelection();

    this.updateResponse();
  }

  validateSelection() {
    if (this.field.required && (!this.localResponse.selectedOptionIds || this.localResponse.selectedOptionIds.length === 0)) {
      this.showValidationError = true;
      this.validationErrorMessage = `${this.field.label} is required`;
    } else {
      this.showValidationError = false;
      this.validationErrorMessage = '';
    }
  }

  updateResponse() {
    this.$emit('update:response', this.localResponse);
  }
}
</script>

<style scoped>
.checkbox-field-response {
  width: 100%;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.field-label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0 0 4px 0;
}

.required-indicator {
  color: #f44336;
  margin-left: 4px;
}

.field-description {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 12px;
}
</style>
